import { useCallback, useEffect, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import Paginator from '../paginator';
import { apiurl } from '../../utils/constants';
import ReloadButton from '../../utils/reload';

function ListOrde({ onMenuItemClick }) {

	const [orders, setOrders] = useState([]);
	const { mail } = useSelector((state) => state.user);

	const [paginatedOrde, setPaginatedOrde] = useState([]);
	const [totalFiltered, setTotalFiltered] = useState(0);
	const [currentPage, setCurrentPage] = useState(1);
	const [cc, setCC] = useState([]);
	const itemsPerPage = 10;

	//Filtros
	const [filtCode, setFiltCode] = useState('');
	const [filtSoli, setFiltSoli] = useState('');
	const [filtCC, setFiltCC] = useState('');
	const [filtStat, setFiltStat] = useState('');
	const [filtCardName, setFiltCardName] = useState('');
	const [filtSentStart, setFiltSentStart] = useState('');
	const [filtSentEnd, setFiltSentEnd] = useState('');
	const [filtEnvio, setFiltEnvio] = useState('');
	const [filtEnvioStart, setFiltEnvioStart] = useState('');
	const [filtEnvioEnd, setFiltEnvioEnd] = useState('');

	const loadOrders = useCallback(async () => {
		try {
			const response = await fetch(apiurl + 'queryAux', {
				method: 'POST',
				credentials: 'include',
				headers: { 'Content-Type': 'application/json' },
				body: JSON.stringify({
					user: mail,
					query: `SELECT orde.docu, orde.stat, orde.sent, orde.done,
								orde.sent_supp, orde.sent_error, orde.prov, orde.card, orde.comm,
								soli.docu AS soli_docu, soli.cent, cent.name AS cc_name
							FROM ord__form orde
							LEFT JOIN req__soli soli ON soli.entry = orde.entr
							LEFT JOIN req__cent cent ON cent.code = soli.cent
							ORDER BY orde.sent DESC`
				})
			});

			if (!response.ok) throw new Error(`Error: ${response.status} ${response.statusText}`);
			const data = await response.json();
			setOrders(Array.isArray(data) ? data : []);
		} catch (error) {
			console.error('Error en loadOrders:', error);
		}
	}, [mail]);

	const loadListCC = useCallback(async () => {
		try {
			const response = await fetch(apiurl + 'queryAux', {
				method: 'POST',
				credentials: 'include',
				headers: { 'Content-Type': 'application/json' },
				body: JSON.stringify({
					user: mail,
					query: `SELECT code, name FROM req__cent;`
				})
			});

			if (response.ok) {
				const data = await response.json();
				setCC(data.map(item => ({ code: item.code, name: item.name })));
			} else {
				console.error('Error al obtener los CC');
			}
		} catch (error) {
			console.error('Error en la solicitud:', error);
		}
	}, [mail]);

	useEffect(() => {
		loadListCC();
		loadOrders();
	}, [loadListCC, loadOrders]);

	const providers = useMemo(() => {
		const map = new Map();
		orders.forEach(o => { if (o.prov && !map.has(o.prov)) map.set(o.prov, o.card); });
		return Array.from(map, ([code, name]) => ({ code, name }));
	}, [orders]);

	const filtCCMatch = cc.find(c => c.name === filtCC || String(c.code) === filtCC);
	const filtProvMatch = providers.find(p => p.name === filtCardName || String(p.code) === filtCardName);

	useEffect(() => {
		const filteredData = orders.filter(orde => {
			const matchesCode = !filtCode || String(orde.docu).toLowerCase().includes(filtCode.toLowerCase());
			const matchesSoli = !filtSoli || String(orde.soli_docu ?? '').toLowerCase().includes(filtSoli.toLowerCase());
			const matchesCC = !filtCC || (filtCCMatch
				? String(orde.cent) === String(filtCCMatch.code)
				: (String(orde.cent ?? '').toLowerCase().includes(filtCC.toLowerCase()) || String(orde.cc_name ?? '').toLowerCase().includes(filtCC.toLowerCase())));
			const matchesStat = !filtStat || orde.stat === filtStat;
			const matchesCardName = !filtCardName || (filtProvMatch
				? String(orde.prov) === String(filtProvMatch.code)
				: (String(orde.prov ?? '').toLowerCase().includes(filtCardName.toLowerCase()) || String(orde.card ?? '').toLowerCase().includes(filtCardName.toLowerCase())));
			const matchesSentStart = !filtSentStart || (orde.sent && orde.sent.slice(0, 10) >= filtSentStart);
			const matchesSentEnd = !filtSentEnd || (orde.sent && orde.sent.slice(0, 10) <= filtSentEnd);

			const matchesEnvio = !filtEnvio
				|| (filtEnvio === 'pend' && !orde.sent_supp)
				|| (filtEnvio === 'ok' && orde.sent_supp && !orde.sent_error)
				|| (filtEnvio === 'err' && orde.sent_error);
			const matchesEnvioStart = !filtEnvioStart || (orde.sent_supp && orde.sent_supp.slice(0, 10) >= filtEnvioStart);
			const matchesEnvioEnd = !filtEnvioEnd || (orde.sent_supp && orde.sent_supp.slice(0, 10) <= filtEnvioEnd);

			return matchesCode && matchesSoli && matchesCC && matchesStat && matchesCardName
				&& matchesSentStart && matchesSentEnd
				&& matchesEnvio && matchesEnvioStart && matchesEnvioEnd;
		});

		const pageHasData = filteredData.length > (currentPage - 1) * itemsPerPage;
		const pageToUse = pageHasData ? currentPage : 1;
		if (pageToUse !== currentPage) setCurrentPage(pageToUse);

		const startIndex = (pageToUse - 1) * itemsPerPage;
		const endIndex = startIndex + itemsPerPage;

		setPaginatedOrde(filteredData.slice(startIndex, endIndex));
		setTotalFiltered(filteredData.length);
	}, [orders, currentPage, filtCode, filtSoli, filtCC, filtCCMatch, filtStat, filtCardName, filtProvMatch,
		filtSentStart, filtSentEnd, filtEnvio, filtEnvioStart, filtEnvioEnd]);

	return (
		<form>
			<style jsx="true"> {`.requestTable small.ellipsis{ display: block; max-width: 800px; margin-top: 10px; }`} </style>
			<main>
				<h1 data-icon=''>Órdenes</h1>
				<nav>
					{
						paginatedOrde.map(orde => (
							<article key={orde.docu}>
								<div>
									<h2>
										<div>{orde.prov} {orde.card}</div>
										<div>{orde.cc_name ? `${orde.cent}. ${orde.cc_name}` : '-'}</div>
										<div>{orde.sent ? orde.sent.slice(0, 10).split('-').reverse().join('/') : '-'}</div>
									</h2>
								</div>
								<table className='requestTable'>
									<tbody>
										<tr>
											<th>{orde.docu}</th>
											<td>
												<b>{orde.soli_docu ? `SP ${orde.soli_docu}` : '-'}</b>
												{orde.comm ? <small className='ellipsis'>{orde.comm}</small> : <></>}
											</td>
											<td className={`state ${orde.stat === 'O' ? 'red' : 'gre'}`}>
												<mark>{orde.stat === 'O' ? 'Abierta' : 'Cerrada'}</mark>
												{orde.done ? <time>{orde.done.slice(0, 10).split('-').reverse().join('/')}</time> : <></>}
												{
													orde.sent_supp
														? <figure className={orde.sent_error ? 'ora' : 'blu'} title={orde.sent_error ?? `Enviado al proveedor el ${orde.sent_supp}`}></figure>
														: <></>
												}
											</td>
										</tr>
									</tbody>
								</table>
							</article>
						))
					}
				</nav>
				<nav>
					<article>
						<Paginator totalItems={totalFiltered} itemsPerPage={itemsPerPage} currentPage={currentPage} onPageChange={setCurrentPage} />
					</article>
				</nav>
			</main>
			<aside>
				<article>
					<h2>Acciones</h2>
					<fieldset>
						<ReloadButton onReload={loadOrders} />
						<input type="button" value="Ver Estadísticas" onClick={() => onMenuItemClick('statOrde')} />
					</fieldset>
				</article>
				<article>
					<h2>Filtros </h2>
					<fieldset>
						<legend>Información </legend>
						<input type="search" placeholder="Orden" value={filtCode} onChange={e => setFiltCode(e.target.value)} />
						<input type="search" placeholder="Solicitud" value={filtSoli} onChange={e => setFiltSoli(e.target.value)} />
						<input type="search" placeholder="Centro de costos" list="cent" value={filtCC} onChange={e => setFiltCC(e.target.value)} />
						<datalist id="cent"> {cc
							.slice()
							.sort((a, b) => (a.name || '').localeCompare(b.name || ''))
							.map((c) => (
								<option key={c.code} value={c.code}>{c.name}</option>
							))} </datalist>
						<select value={filtStat} onChange={e => setFiltStat(e.target.value)}>
							<option value=''> Estado </option>
							<option value='O'> Abierta </option>
							<option value='C'> Cerrada </option>
						</select>
					</fieldset>

					<fieldset>
						<legend>Emisión</legend>
						<input type="text" placeholder="Proveedor" list="providers" value={filtCardName} onChange={e => setFiltCardName(e.target.value)} />
						<datalist id="providers">
							{providers.slice().sort((a, b) => (a.name || '').localeCompare(b.name || '')).map(p => (
								<option key={p.code} value={p.code}>{p.name}</option>
							))}
						</datalist>
						<input type="date" value={filtSentStart} onChange={e => setFiltSentStart(e.target.value)} />
						<input type="date" value={filtSentEnd} onChange={e => setFiltSentEnd(e.target.value)} />
					</fieldset>

					<fieldset>
						<legend>Envío al proveedor</legend>
						<select value={filtEnvio} onChange={e => setFiltEnvio(e.target.value)}>
							<option value=''> Envío </option>
							<option value='pend'> No enviada </option>
							<option value='ok'> Enviada sin error </option>
							<option value='err'> Error al enviar </option>
						</select>
						<input type="date" value={filtEnvioStart} onChange={e => setFiltEnvioStart(e.target.value)} />
						<input type="date" value={filtEnvioEnd} onChange={e => setFiltEnvioEnd(e.target.value)} />
					</fieldset>
				</article>
			</aside>
		</form>
	);
}

export default ListOrde;
